import type { FC, ReactNode } from 'react'
import type { LitToken } from '@/components/motion/lit-text'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import LitText from '@/components/motion/lit-text'
import Marquee from '@/components/motion/marquee'
import { SplitChars } from '@/components/motion/split-text'
import AboutHero from '@/components/about/about-hero'
import Timeline from '@/components/about/timeline'
import Team from '@/components/about/team'
import Button from '@/components/generals/button'
import Star from '@/components/generals/star'
import { values } from '@/constants/content'
import { editorial, media } from '@/constants/media'

const STORY: Array<LitToken> = [
    'Fashion taught us to replace. We wanted to repair. So we started making',
    { image: media.essentialsLineup, alt: "The SS26 essentials laid out in a row" },
    'essentials — garments engineered to be worn every day, patched, re-dyed',
    { image: editorial.reDyedSwatches, alt: "Fabric swatches in dyed tones laid out beside pencils" },
    'and passed on — until the idea of a new season feels a little absurd.'
]

const About: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='About us'
                description="Elio is a small Berlin studio making very few, very heavy things: numbered runs of essentials, cut in Porto and repaired for life since 2019."
                ogImage={media.essentialsLineup}
            />

            <AboutHero />

            <section aria-labelledby='story-title' className='py-24 gutter md:py-36'>
                <div className='grid gap-10 md:grid-cols-12 md:gap-8'>
                    <p id='story-title' data-reveal='fade' className='label-mono text-acid md:col-span-3'>
                        (01) — Why we exist
                    </p>

                    <LitText
                        tokens={STORY}
                        className='font-sans text-[clamp(28px,4vw,62px)] font-medium leading-[1.06] tracking-[-0.035em] text-bone md:col-span-9'
                    />
                </div>
            </section>

            <Timeline />

            <div aria-hidden='true' className='overflow-hidden bg-bone py-6 text-ink md:py-8'>
                <Marquee speed={55} velocity={0.16}>
                    {values.map(value => (
                        <span key={value} className='flex items-center gap-10 pr-10 font-heading text-[clamp(56px,9vw,150px)] leading-none'>
                            {value}

                            <Star />
                        </span>
                    ))}
                </Marquee>
            </div>

            <Team />

            <section aria-labelledby='visit-title' className='bg-acid py-24 text-ink gutter md:py-32'>
                <div className='grid gap-10 md:grid-cols-12 md:items-end'>
                    <h2 id='visit-title' data-reveal='chars' className='font-heading text-[clamp(56px,8vw,136px)] uppercase leading-[0.84] md:col-span-7'>
                        <span className='block'>
                            <SplitChars text='Come see' />
                        </span>

                        <span className='block'>
                            <SplitChars text='the rail.' />
                        </span>
                    </h2>

                    <div className='md:col-span-4 md:col-start-9 md:text-right'>
                        <p data-reveal='fade' className='lead text-ink/80'>
                            Berlin · Tokyo · New York — open daily, by appointment after dark.
                        </p>

                        <div data-reveal='fade' className='mt-8'>
                            <Button href='/contact#stores' variant='ink'>
                                Find a store
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default About
